import { useTheme } from "../context/ThemeContext";
import { useDashboardData } from "../hooks/useDashboardData";
import { getRiskColor, getRiskIconColor, getProbabilityColor } from "../lib/styleHelpers";
import {
  Spinner,
  ArrowClockwise,
  ChartLine,
  Clock,
  TrendUp,
  Gear,
  Moon,
  Sun,
  Star,
  Briefcase,
  Target,
  CalendarBlank,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { DashboardTab } from "../components/dashboard/DashboardTab";
import { HistoryTab } from "../components/dashboard/HistoryTab";
import { TrendsTab } from "../components/dashboard/TrendsTab";
import { SettingsTab } from "../components/dashboard/SettingsTab";
import { WatchlistTab } from "../components/dashboard/WatchlistTab";
import { PortfolioTab } from "../components/dashboard/PortfolioTab";
import { PerformanceTab } from "../components/dashboard/PerformanceTab";
import { DealsLogTab } from "../components/dashboard/DealsLogTab";
import { TrackingModal } from "../components/dashboard/TrackingModal";

const TAB_CLASS_DARK = "data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-purple-500 data-[state=active]:text-purple-500 rounded-none px-4 h-12 text-gray-400";
const TAB_CLASS_LIGHT = "data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-purple-500 data-[state=active]:text-purple-500 rounded-none px-4 h-12 text-gray-600";

export default function Dashboard() {
  const { toggleTheme, isDark } = useTheme();
  const data = useDashboardData();
  const tabClass = isDark ? TAB_CLASS_DARK : TAB_CLASS_LIGHT;

  const boundGetRiskColor = (level) => getRiskColor(level, isDark);

  if (data.loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0a0a14]' : 'bg-gray-50'}`} data-testid="loading-screen">
        <div className="text-center">
          <Spinner size={48} className="animate-spin text-purple-500 mx-auto mb-4" />
          <p className={`font-body ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Loading opportunities...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors ${isDark ? 'bg-[#0a0a14]' : 'bg-gray-50'}`} data-testid="dashboard">
      {/* Header */}
      <header className={`sticky top-0 z-50 backdrop-blur-xl border-b ${isDark ? 'bg-[#0a0a14]/80 border-white/5' : 'bg-white/80 border-gray-200'}`} data-testid="header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="/" className="flex items-center gap-3">
              <img src={isDark ? "/logo-dark.png" : "/logo-light.png"} alt="MyQairo" className="h-12 w-auto" />
            </a>
            <div className="flex items-center gap-4">
              {data.stats && (
                <div className="hidden md:flex items-center gap-6 text-sm">
                  <div className="text-center">
                    <p className={`font-body text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Total Analyzed</p>
                    <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{data.stats.total_opportunities}</p>
                  </div>
                  <div className="text-center">
                    <p className={`font-body text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>High Probability</p>
                    <p className="font-bold text-emerald-500">{data.stats.high_probability_count}</p>
                  </div>
                  <div className="text-center">
                    <p className={`font-body text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Avg. Probability</p>
                    <p className="font-bold text-purple-500">{data.stats.average_probability}%</p>
                  </div>
                </div>
              )}
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-full transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
                data-testid="theme-toggle"
              >
                {isDark ? <Sun size={20} className="text-yellow-400" /> : <Moon size={20} className="text-purple-600" />}
              </button>
              <Button
                onClick={() => data.generateOpportunity()}
                disabled={data.generating}
                className="bg-purple-600 hover:bg-purple-700 text-white rounded-full"
                data-testid="generate-btn"
              >
                {data.generating ? (
                  <><Spinner size={18} className="animate-spin mr-2" />Generating...</>
                ) : (
                  <><ArrowClockwise size={18} className="mr-2" />Generate New</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className={`border-b ${isDark ? 'bg-[#0c0c16] border-white/5' : 'bg-white border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={data.activeTab} onValueChange={data.setActiveTab} className="w-full">
            <TabsList className="bg-transparent h-12 p-0 border-0 overflow-x-auto">
              <TabsTrigger value="dashboard" className={tabClass} data-testid="tab-dashboard">
                <ChartLine size={18} className="mr-2" />Dashboard
              </TabsTrigger>
              <TabsTrigger value="history" className={tabClass} data-testid="tab-history">
                <Clock size={18} className="mr-2" />History
              </TabsTrigger>
              <TabsTrigger value="trends" className={tabClass} data-testid="tab-trends">
                <TrendUp size={18} className="mr-2" />Trends
              </TabsTrigger>
              <TabsTrigger value="watchlist" className={tabClass} data-testid="tab-watchlist">
                <Star size={18} className="mr-2" />Watchlist
                {data.watchlist.length > 0 && (
                  <span className="ml-1.5 text-[10px] bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center">
                    {data.watchlist.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="portfolio" className={tabClass} data-testid="tab-portfolio">
                <Briefcase size={18} className="mr-2" />Portfolio
                {data.portfolio.length > 0 && (
                  <span className="ml-1.5 text-[10px] bg-emerald-600 text-white rounded-full w-5 h-5 flex items-center justify-center">
                    {data.portfolio.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="performance" className={tabClass} data-testid="tab-performance">
                <Target size={18} className="mr-2" />Performance
              </TabsTrigger>
              <TabsTrigger value="deals" className={tabClass} data-testid="tab-deals">
                <CalendarBlank size={18} className="mr-2" />Deals Log
              </TabsTrigger>
              <TabsTrigger value="settings" className={tabClass} data-testid="tab-settings">
                <Gear size={18} className="mr-2" />Settings
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {data.activeTab === "dashboard" && (
          <DashboardTab
            todaysOpportunity={data.todaysOpportunity}
            selectedOpportunity={data.selectedOpportunity}
            setSelectedOpportunity={data.setSelectedOpportunity}
            opportunities={data.opportunities}
            generating={data.generating}
            generateOpportunity={data.generateOpportunity}
            getRiskColor={boundGetRiskColor}
            getRiskIconColor={getRiskIconColor}
            getProbabilityColor={getProbabilityColor}
            isDark={isDark}
            watchlistIds={data.watchlistIds}
            onToggleWatchlist={data.toggleWatchlist}
          />
        )}
        {data.activeTab === "history" && (
          <HistoryTab
            opportunities={data.opportunities}
            setSelectedOpportunity={data.setSelectedOpportunity}
            setActiveTab={data.setActiveTab}
            getRiskColor={boundGetRiskColor}
            isDark={isDark}
          />
        )}
        {data.activeTab === "trends" && (
          <TrendsTab trends={data.trends} stats={data.stats} isDark={isDark} />
        )}
        {data.activeTab === "watchlist" && (
          <WatchlistTab
            watchlist={data.watchlist}
            onUpdateItem={data.updateWatchlistItem}
            onRemoveItem={data.removeWatchlistItem}
            onViewOpportunity={data.viewOpportunity}
            onTrackInPortfolio={(item) => data.setTrackingModal(item)}
            portfolioOppIds={data.portfolioOppIds}
            isDark={isDark}
          />
        )}
        {data.activeTab === "portfolio" && (
          <PortfolioTab
            portfolio={data.portfolio}
            summary={data.portfolioSummary}
            onUpdateItem={data.updatePortfolioItem}
            onRemoveItem={data.removePortfolioItem}
            onViewOpportunity={data.viewOpportunity}
            onExportPdf={data.exportPortfolioPdf}
            exportingPdf={data.exportingPdf}
            isDark={isDark}
          />
        )}
        {data.activeTab === "performance" && (
          <PerformanceTab performance={data.performance} isDark={isDark} />
        )}
        {data.activeTab === "deals" && (
          <DealsLogTab
            dealsData={data.dealsData}
            onViewOpportunity={data.viewOpportunity}
            isDark={isDark}
          />
        )}
        {data.activeTab === "settings" && (
          <SettingsTab
            settings={data.settings}
            updateSettings={data.updateSettings}
            savingSettings={data.savingSettings}
            onTestWhatsApp={data.testWhatsApp}
            isDark={isDark}
          />
        )}
      </main>

      {data.trackingModal && (
        <TrackingModal
          item={data.trackingModal}
          isDark={isDark}
          onConfirm={data.trackInPortfolio}
          onClose={() => data.setTrackingModal(null)}
        />
      )}
    </div>
  );
}
